import { useState } from 'react';
import { motion } from 'motion/react';
import { Check, X, Sparkles, Zap } from 'lucide-react';
import { PLANS, PLAN_FEATURES, formatLimit } from '../../../shared/config/plans';
import { useFeatureToggle } from '../../../shared/context/FeatureToggleContext';
import type { PlanFeature } from '../../../shared/config/plans';

interface PricingSectionProps {
  onGetStarted: () => void;
}

export default function PricingSection({ onGetStarted }: PricingSectionProps) {
  const [yearly, setYearly] = useState(false);
  const { isEnabled } = useFeatureToggle();

  if (!isEnabled('pricing')) return null;

  return (
    <section id="pricing" className="py-24 bg-white dark:bg-[#020617] transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
            <Zap className="w-4 h-4" /> Simple Pricing
          </span>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-4">
            Choose the right plan for your business
          </h2>
          <p className="text-slate-600 dark:text-slate-400 max-w-xl mx-auto mb-8">
            Start free and scale as you grow. All plans include core accounting features.
          </p>

          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-3">
            <span className={`text-sm font-medium ${!yearly ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>Monthly</span>
            <button onClick={() => setYearly(!yearly)}
              className="relative w-14 h-7 bg-slate-200 dark:bg-slate-700 rounded-full transition-colors"
              style={yearly ? { backgroundColor: 'var(--color-primary)' } : {}}>
              <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${yearly ? 'translate-x-7' : 'translate-x-0.5'}`} />
            </button>
            <span className={`text-sm font-medium ${yearly ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>
              Yearly <span className="text-emerald-500 text-xs font-bold">Save 17%</span>
            </span>
          </div>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {PLANS.map((plan, i) => (
            <motion.div key={plan.id}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className={`relative rounded-2xl border-2 p-6 transition-all ${
                plan.highlight
                  ? 'border-primary bg-primary/5 dark:bg-primary/10 shadow-xl shadow-primary/10'
                  : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50'
              }`}>
              {plan.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] font-black uppercase tracking-widest px-4 py-1 rounded-full flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Most Popular
                </div>
              )}

              <h3 className="text-lg font-bold text-slate-900 dark:text-white">{plan.name}</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 mb-4">{plan.description}</p>

              <div className="mb-6">
                <span className="text-4xl font-black text-slate-900 dark:text-white">
                  ${yearly ? Math.round(plan.yearlyPrice / 12) : plan.price}
                </span>
                <span className="text-sm text-slate-400">/mo</span>
                {yearly && plan.price > 0 && (
                  <p className="text-xs text-emerald-500 font-bold mt-1">
                    ${plan.yearlyPrice}/year (save ${plan.price * 12 - plan.yearlyPrice})
                  </p>
                )}
              </div>

              <button onClick={onGetStarted}
                className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all ${
                  plan.highlight
                    ? 'bg-primary text-white hover:bg-primary-dark shadow-lg shadow-primary/20'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}>
                {plan.price === 0 ? 'Start Free' : 'Get Started'}
              </button>

              <div className="mt-6 space-y-2.5">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Limits</p>
                <LimitRow label="Users" value={plan.maxUsers} />
                <LimitRow label="Customers" value={plan.maxCustomers} />
                <LimitRow label="Invoices/mo" value={plan.maxInvoices} />
                <LimitRow label="Employees" value={plan.maxEmployees} />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Feature comparison table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-slate-200 dark:border-slate-800">
                <th className="text-left py-3 px-4 text-slate-500 dark:text-slate-400 font-bold">Feature</th>
                {PLANS.map(p => (
                  <th key={p.id} className={`text-center py-3 px-4 font-bold ${p.highlight ? 'text-primary' : 'text-slate-700 dark:text-slate-300'}`}>
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PLAN_FEATURES.map((f: PlanFeature) => (
                <tr key={f.feature} className="border-b border-slate-100 dark:border-slate-800/50">
                  <td className="py-2.5 px-4 text-slate-600 dark:text-slate-400">{f.label}</td>
                  {(['starter', 'professional', 'business', 'enterprise'] as const).map(planId => {
                    const val = f[planId];
                    return (
                      <td key={planId} className="text-center py-2.5 px-4">
                        {val === true ? <Check className="w-4 h-4 text-emerald-500 mx-auto" /> :
                         val === false ? <X className="w-4 h-4 text-slate-300 dark:text-slate-600 mx-auto" /> :
                         <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{formatLimit(val)}</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function LimitRow({ label, value }: { label: string; value: number }) {
  const display = value === -1 ? 'Unlimited' : value === 0 ? '—' : String(value);
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className={`font-bold ${value === -1 ? 'text-emerald-500' : value === 0 ? 'text-slate-300' : 'text-slate-700 dark:text-slate-300'}`}>
        {display}
      </span>
    </div>
  );
}
